import React, { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge';
import { FormattedMessage } from '@atlassian/jira-intl';
import type { FormStore } from '@atlassian/proforma-common-core/form-system-stores';
import { useFormIndexActions, useSelectedFormStoreState } from '@atlassian/proforma-form-system';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics';
import { useShowNotice } from '@atlassian/proforma-utils/show-notice';
import { useSelectFormNoDiscardChangeNotice } from '../use-handle-form-selected/index.tsx';
import { messages } from './messages.tsx';
import { DeleteFormResult, type HandleDeleteForm } from './types.tsx';

export const useHandleDeleteForm = (
	loadFormStore: (formId: number) => Promise<FormStore>,
	disableIssueViewFullPageDropzoneCallback: (isFormInEditMode: boolean) => void,
): HandleDeleteForm => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { deleteForm, reportError } = useFormIndexActions();
	const selectFormNoDiscardChangeNotice = useSelectFormNoDiscardChangeNotice(
		loadFormStore,
		disableIssueViewFullPageDropzoneCallback,
	);
	const { selectedFormStore } = useSelectedFormStoreState();
	const { showNotice } = useShowNotice();

	const handleDeleteForm = useCallback(
		(formId: number, formName: string) =>
			new Promise<DeleteFormResult>((resolve) => {
				showNotice({
					title: <FormattedMessage {...messages.deleteModalTitle} values={{ formName }} />,
					appearance: 'warning',
					body: <FormattedMessage {...messages.deleteModalBody} />,
					closeText: <FormattedMessage {...messages.deleteModalCancel} />,
					confirmText: <FormattedMessage {...messages.deleteModalConfirm} />,
					onConfirmHandler: async () => {
						try {
							const deletedProjectFormId = await deleteForm(formId);

							if (formId === selectedFormStore?.formId) {
								selectFormNoDiscardChangeNotice();
							}

							fireTrackAnalytics(
								createAnalyticsEvent({}),
								`${ActionSubject.FORM} ${Action.DELETED}`,
								{
									formId,
									templateFormId: deletedProjectFormId,
								},
							);
							resolve(DeleteFormResult.Success);
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
						} catch (error: any) {
							reportError(error, {
								id: 'deleteForm',
								attributes: { formId },
							});
							resolve(DeleteFormResult.Failure);
						}
					},
					onCloseHandler: () => resolve(DeleteFormResult.Cancelled),
				});
			}),
		[
			createAnalyticsEvent,
			deleteForm,
			reportError,
			selectFormNoDiscardChangeNotice,
			selectedFormStore?.formId,
			showNotice,
		],
	);

	return handleDeleteForm;
};
